import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { Box, ButtonGroup, Divider, Icon, IconButton, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled, IconErrorFilled } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../../../hooks/queries/properties';

import { emitJumpToErrorInteractionEvent, EVENT_IDS } from '../../../../../../analytics/events/reviewPage';
import { useAuth } from '../../../../../AuthProvider';

import type { ErrorKey } from './errorMapping';

type Props = {
  errors: Array<ErrorKey>;
  currentErrorIndex: number;
  errorMessage: string;
  onJumpToError: () => void;
  onNext: () => void;
  onPrevious: () => void;
};

const ValidationErrorGuide = ({
  errors,
  currentErrorIndex,
  errorMessage,
  onJumpToError,
  onNext,
  onPrevious,
}: Props): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  if (errors.length === 0) {
    return null;
  }

  const totalErrors = errors.length;
  const displayIndex = currentErrorIndex + 1;

  const trackInteraction = useCallback(() => {
    if (listingDetails && user) {
      emitJumpToErrorInteractionEvent({ currentProperty: listingDetails, user });
    }
  }, [listingDetails, user]);

  const handleJumpToErrorClick = useCallback(() => {
    onJumpToError();
    trackInteraction();
  }, [onJumpToError, trackInteraction]);

  const handleNextClick = useCallback(() => {
    onNext();
    trackInteraction();
  }, [onNext, trackInteraction]);

  const handlePreviousClick = useCallback(() => {
    onPrevious();
    trackInteraction();
  }, [onPrevious, trackInteraction]);

  return (
    <Box
      role="alert"
      aria-label={`Validation error ${displayIndex} of ${totalErrors}`}
      data-testid="ValidationErrorGuide"
      css={{
        display: 'flex',
        alignItems: { base: 'flex-start', lg: 'center' },
        justifyContent: { base: 'flex-start', lg: 'center' },
        gap: '300',
        width: '100%',
      }}
    >
      <Icon
        size="md"
        render={<IconErrorFilled />}
        css={{
          color: 'icon.feedback.critical.strong',
          flexShrink: 0,
          display: { base: 'block', lg: 'none', xl: 'block' },
        }}
      />
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', lg: 'row' },
          alignItems: { base: 'flex-start', lg: 'center' },
          gap: { base: '0', lg: '300' },
        }}
      >
        <Text
          css={{
            color: 'text.feedback.critical.strong',
            lineHeight: { base: '1.5', lg: 'normal' },
          }}
        >
          {errorMessage}
        </Text>
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '200',
            width: { base: '100%', lg: 'auto' },
            flexShrink: 0,
          }}
        >
          <TextButton
            data-eventid={EVENT_IDS.JUMP_TO_ERROR}
            textStyle="body-bold"
            onClick={handleJumpToErrorClick}
            css={{
              flexShrink: 0,
            }}
          >
            Jump to error
          </TextButton>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Divider orientation="vertical" css={{ height: '300', marginRight: '200' }} />
            <Text
              textStyle="body-sm"
              css={{
                color: 'text.subtle',
                flexShrink: 0,
              }}
            >
              {displayIndex} of {totalErrors}
            </Text>
            {totalErrors > 1 && (
              <ButtonGroup aria-label="error navigation" css={{ gap: '0', marginLeft: '200' }}>
                <IconButton
                  data-eventid={EVENT_IDS.JUMP_TO_ERROR}
                  size="sm"
                  emphasis="bare"
                  tone="neutral-fixed"
                  onClick={handlePreviousClick}
                  disabled={currentErrorIndex === 0}
                  icon={
                    <Icon>
                      <IconChevronUpFilled />
                    </Icon>
                  }
                  title="Previous error"
                />
                <IconButton
                  data-eventid={EVENT_IDS.JUMP_TO_ERROR}
                  size="sm"
                  emphasis="bare"
                  tone="neutral-fixed"
                  onClick={handleNextClick}
                  disabled={currentErrorIndex === totalErrors - 1}
                  icon={
                    <Icon>
                      <IconChevronDownFilled />
                    </Icon>
                  }
                  title="Next error"
                />
              </ButtonGroup>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ValidationErrorGuide;
